import { useMemo, useState } from "react";
import { addressEntries, addressLineTabs } from "@/mocks/addressEntries";
import { useCopy } from "@/hooks/useCopy";
import SectionHeading from "./SectionHeading";

const statusMap: Record<string, { label: string; cls: string; dot: string }> = {
  online: { label: "当前可用", cls: "bg-primary-100 text-primary-700", dot: "bg-primary-500" },
  backup: { label: "备用可用", cls: "bg-secondary-100 text-secondary-900", dot: "bg-secondary-500" },
};

export default function AddressSection() {
  const [activeTab, setActiveTab] = useState("all");
  const [keyword, setKeyword] = useState("");
  const { copiedId, copy } = useCopy();

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return addressEntries.filter((item) => {
      const matchLine = activeTab === "all" || item.line === activeTab;
      const matchKw =
        kw.length === 0 ||
        item.name.toLowerCase().includes(kw) ||
        item.url.toLowerCase().includes(kw) ||
        item.note.toLowerCase().includes(kw) ||
        item.tags.join(" ").toLowerCase().includes(kw);
      return matchLine && matchKw;
    });
  }, [activeTab, keyword]);

  return (
    <section id="addresses" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="最新可用地址"
          title="当前收录的可用入口，按线路分类排列"
          desc="下列入口按主线路、备用线路、移动优化与镜像四类整理，并标注了访问速度与更新时间。建议优先使用主线路，遇到打不开时按顺序切换即可。"
        />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 overflow-x-auto no-scrollbar max-w-full">
            {addressLineTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "text-foreground-600 hover:text-primary-600 hover:bg-background-200/70"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索线路名称或地址"
              className="w-full h-11 pl-9 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
            />
          </div>
        </div>

        <p className="text-xs md:text-sm text-foreground-500 mb-5">
          共找到 <strong className="text-primary-600">{filtered.length}</strong> 条入口 · 数据核验时间以页面更新条为准
        </p>

        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有匹配的入口，试试其他关键词或切换线路分类。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filtered.map((item, index) => {
              const st = statusMap[item.status] ?? statusMap.backup;
              return (
                <article
                  key={item.id}
                  className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 hover:border-primary-300 transition-colors animate-fade-up"
                  style={{ animationDelay: `${index * 0.04}s` }}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                        <i className="ri-link-m"></i>
                      </span>
                      <h3 className="font-heading font-semibold text-base text-foreground-950">{item.name}</h3>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${st.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                      {st.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 rounded-md bg-background-100 border border-background-200 px-3 py-2.5">
                    <code className="flex-1 text-sm text-foreground-900 tracking-wide break-all">{item.url}</code>
                    <button
                      type="button"
                      onClick={() => copy(item.url, item.id)}
                      aria-label={`复制 ${item.name} 地址`}
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                    >
                      <i className={copiedId === item.id ? "ri-check-line" : "ri-file-copy-line"}></i>
                      {copiedId === item.id ? "已复制" : "复制"}
                    </button>
                  </div>

                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed flex-1">{item.note}</p>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-speed-up-line text-accent-600"></i>速度 {item.speed}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-calendar-check-line text-secondary-500"></i>更新 {item.updatedAt}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-8 rounded-lg bg-accent-50 border border-accent-200 p-4 md:p-5 flex items-start gap-3">
          <i className="ri-lightbulb-line text-accent-600 text-lg mt-0.5"></i>
          <p className="text-sm text-foreground-700 leading-relaxed">
            使用建议：先用主线路，速度最快；若某个时段打不开，切到备用线路或移动优化线路即可。
            无需反复刷新同一条地址，换一条通常更省时间。
          </p>
        </div>
      </div>
    </section>
  );
}